import { memo, useRef, useEffect, useState, useCallback, type FC } from 'react';
import styles from './WebRTCVideoPlayer.module.css';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed';

export interface WebRTCVideoPlayerProps {
  /** WHEP endpoint URL (e.g., http://localhost:8889/stream/whep) */
  whepUrl: string;
  /** Auto-reconnect on connection failure */
  autoReconnect?: boolean;
  /** Reconnect interval in milliseconds */
  reconnectInterval?: number;
  /** Maximum reconnect attempts (0 = unlimited) */
  maxReconnectAttempts?: number;
  /** Callback when connection status changes */
  onStatusChange?: (status: ConnectionStatus) => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
  /** Custom className for video element */
  className?: string;
  /** Muted state */
  muted?: boolean;
  /** Show controls */
  controls?: boolean;
}

/**
 * WebRTCVideoPlayer - WebRTC WHEP Video Player Component
 * Supports MediaMTX WHIP/WHEP protocol for low-latency video streaming
 */
export const WebRTCVideoPlayer: FC<WebRTCVideoPlayerProps> = memo(({
  whepUrl,
  autoReconnect = true,
  reconnectInterval = 3000,
  maxReconnectAttempts = 0,
  onStatusChange,
  onError,
  className,
  muted = true,
  controls = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const resourceUrlRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');

  const updateStatus = useCallback((newStatus: ConnectionStatus) => {
    if (!isMountedRef.current) return;
    setStatus(newStatus);
    onStatusChange?.(newStatus);
  }, [onStatusChange]);

  const handleError = useCallback((error: Error) => {
    if (!isMountedRef.current) return;
    console.error('[WebRTCVideoPlayer] Error:', error.message);
    onError?.(error);
  }, [onError]);

  const cleanup = useCallback(async () => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Send DELETE request to release WHEP resource
    if (resourceUrlRef.current) {
      try {
        await fetch(resourceUrlRef.current, { method: 'DELETE' });
      } catch {
        // Ignore cleanup errors
      }
      resourceUrlRef.current = null;
    }

    // Clear video source
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!isMountedRef.current) return;

    await cleanup();
    updateStatus('connecting');

    try {
      // Create RTCPeerConnection with STUN servers
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
        ],
      });
      peerConnectionRef.current = pc;

      // Handle incoming tracks
      pc.ontrack = (event) => {
        if (!isMountedRef.current) return;
        console.log('[WebRTCVideoPlayer] Track received:', event.track.kind);

        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        if (!isMountedRef.current) return;
        console.log('[WebRTCVideoPlayer] Connection state:', pc.connectionState);

        switch (pc.connectionState) {
          case 'connected':
            reconnectAttemptsRef.current = 0;
            updateStatus('connected');
            break;
          case 'disconnected':
          case 'failed':
            updateStatus('failed');
            if (autoReconnect) {
              scheduleReconnect();
            }
            break;
          case 'closed':
            updateStatus('disconnected');
            break;
        }
      };

      // Handle ICE connection state changes
      pc.oniceconnectionstatechange = () => {
        if (!isMountedRef.current) return;
        console.log('[WebRTCVideoPlayer] ICE state:', pc.iceConnectionState);
      };

      // Add transceivers for receiving audio and video
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      // Create and set local description (SDP offer)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering to complete or timeout
      await waitForIceGathering(pc);

      // Send SDP offer to WHEP endpoint
      const response = await fetch(whepUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
        },
        body: pc.localDescription?.sdp,
      });

      if (!response.ok) {
        throw new Error(`WHEP request failed: ${response.status} ${response.statusText}`);
      }

      // Store resource URL for cleanup
      const location = response.headers.get('Location');
      if (location) {
        // Handle relative URLs
        resourceUrlRef.current = location.startsWith('http')
          ? location
          : new URL(location, whepUrl).href;
      }

      // Get and set remote description (SDP answer)
      const answerSdp = await response.text();
      const answer = new RTCSessionDescription({
        type: 'answer',
        sdp: answerSdp,
      });
      await pc.setRemoteDescription(answer);

      console.log('[WebRTCVideoPlayer] WHEP connection established');

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      handleError(err);
      updateStatus('failed');

      if (autoReconnect) {
        scheduleReconnect();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- circular dependency with scheduleReconnect
  }, [whepUrl, autoReconnect, cleanup, updateStatus, handleError]);

  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;
    if (!autoReconnect) return;

    // Check max reconnect attempts
    if (maxReconnectAttempts > 0 && reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('[WebRTCVideoPlayer] Max reconnect attempts reached');
      return;
    }

    reconnectAttemptsRef.current += 1;
    console.log(`[WebRTCVideoPlayer] Scheduling reconnect attempt ${reconnectAttemptsRef.current}`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, reconnectInterval);
  }, [autoReconnect, maxReconnectAttempts, reconnectInterval, connect]);

  // Connect on mount and when URL changes
  useEffect(() => {
    isMountedRef.current = true;
    reconnectAttemptsRef.current = 0;
    connect();

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [whepUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.container}>
      <video
        ref={videoRef}
        className={`${styles.video} ${className ?? ''}`}
        autoPlay
        playsInline
        muted={muted}
        controls={controls}
      />
      {status === 'connecting' && (
        <div className={styles.overlay}>
          <div className={styles.spinner} />
          <span className={styles.statusText}>Connecting...</span>
        </div>
      )}
      {status === 'failed' && (
        <div className={styles.overlay}>
          <span className={styles.statusText}>
            Connection failed
            {autoReconnect && <><br />Reconnecting...</>}
          </span>
        </div>
      )}
    </div>
  );
});

WebRTCVideoPlayer.displayName = 'WebRTCVideoPlayer';

/**
 * Wait for ICE gathering to complete with timeout
 */
async function waitForIceGathering(
  pc: RTCPeerConnection,
  timeout = 2000
): Promise<void> {
  if (pc.iceGatheringState === 'complete') {
    return;
  }

  return new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', checkState);
      resolve();
    }, timeout);

    const checkState = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timeoutId);
        pc.removeEventListener('icegatheringstatechange', checkState);
        resolve();
      }
    };

    pc.addEventListener('icegatheringstatechange', checkState);
  });
}
